import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Collapse, Form, Input, Popconfirm, Select, Space, Tooltip } from 'antd';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { AppModal } from '@/components/ui/AppModal';
import { PROTECTED_ROLES, Roles } from '@/core/utils/roles';
import { rolesInfoData } from '@/core/utils/rolesInfo';
import { validateTelegramUsername } from '@/utils/validators';
import type { ExtendedUser, UserDepartment } from '../query/users.hooks';
import { useGetUserMeta } from '../query/users.hooks';

const { Option } = Select;

const ALL_DISPLAYABLE_ROLES: { value: string; label: string }[] = [
  { value: Roles.driver, label: 'Driver' },
  { value: Roles.operator, label: 'Operator' },
  { value: Roles.seniorOperator, label: 'Senior Operator' },
  { value: Roles.auditor, label: 'Auditor' },
  { value: Roles.accountant, label: 'Accountant' },
  { value: Roles.financeManager, label: 'Finance Manager' },
  { value: Roles.storekeeper, label: 'Storekeeper' },
  { value: Roles.manager, label: 'Manager' },
];

export interface EditUserPermissions {
  canEditNameAndTelegram: boolean;
  canEditDepartments: boolean;
  canEditRoles: boolean;
  canBan: boolean;
}

export interface EditUserSubmitData {
  name: string;
  telegramName: string;
  departmentIds: number[];
  roles: string[];
}

interface EditUserModalProps {
  visible: boolean;
  user: ExtendedUser | null;
  permissions: EditUserPermissions;
  assignableRoles: string[];
  loading: boolean;
  banLoading: boolean;
  onCancel: () => void;
  onSubmit: (data: EditUserSubmitData) => void;
  onBanUnban: () => void;
}

export const EditUserModalView: React.FC<EditUserModalProps> = ({
  visible,
  user,
  permissions,
  assignableRoles,
  loading,
  banLoading,
  onCancel,
  onSubmit,
  onBanUnban,
}) => {
  const [form] = Form.useForm<{
    name: string;
    telegramName: string;
    departmentIds: number[];
    roles: string[];
  }>();
  const { data: userMeta } = useGetUserMeta({});
  const [rolesInfoVisible, setRolesInfoVisible] = useState(false);

  const initialDepartmentIds = useMemo(() => {
    if (!user) return [];
    return (user.departments_users || [])
      .filter(
        (d): d is UserDepartment & { department: NonNullable<UserDepartment['department']> } =>
          d.department !== null
      )
      .map((d) => d.departmentId);
  }, [user]);

  const filteredInitialRoles = useMemo(
    () =>
      ((user?.roles as string[]) || []).filter(
        (role) => !PROTECTED_ROLES.includes(role as (typeof PROTECTED_ROLES)[number])
      ),
    [user?.roles]
  );

  useEffect(() => {
    if (visible && user) {
      form.setFieldsValue({
        name: user.name || '',
        telegramName: user.telegramName || '',
        departmentIds: initialDepartmentIds,
        roles: filteredInitialRoles,
      });
    }
  }, [visible, user, form, initialDepartmentIds, filteredInitialRoles]);

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const protectedRolesUserHad = ((user?.roles as string[]) || []).filter((role) =>
          PROTECTED_ROLES.includes(role as (typeof PROTECTED_ROLES)[number])
        );
        const finalRoles = [...new Set([...values.roles, ...protectedRolesUserHad])];
        onSubmit({
          name: values.name.trim(),
          telegramName: values.telegramName?.trim() || '',
          departmentIds: values.departmentIds,
          roles: finalRoles,
        });
      })
      .catch(() => undefined);
  };

  const hasAnyEditPermission =
    permissions.canEditNameAndTelegram ||
    permissions.canEditDepartments ||
    permissions.canEditRoles;

  return (
    <AppModal
      open={visible}
      title="Edit user"
      okText="Save"
      confirmLoading={loading}
      onCancel={onCancel}
      onOk={handleSubmit}
      destroyOnHidden
      maskClosable={!loading && !banLoading}
      footer={(_, { OkBtn, CancelBtn }) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {permissions.canBan && user && (
              <Popconfirm
                title={`Are you sure you want to ${user.blocked ? 'unban' : 'ban'} this user?`}
                onConfirm={onBanUnban}
              >
                <Button
                  type={user.blocked ? 'default' : 'primary'}
                  danger={!user.blocked}
                  loading={banLoading}
                >
                  {user.blocked ? 'Unban' : 'Ban'}
                </Button>
              </Popconfirm>
            )}
          </div>
          <Space>
            <CancelBtn />
            {hasAnyEditPermission && <OkBtn />}
          </Space>
        </div>
      )}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Email">
          <Input value={user?.email || ''} disabled />
        </Form.Item>

        <Form.Item
          className="app-form-item"
          label="Name"
          name="name"
          rules={
            permissions.canEditNameAndTelegram
              ? [{ required: true, message: 'Please input user name' }]
              : undefined
          }
        >
          <Input placeholder="Enter user name" disabled={!permissions.canEditNameAndTelegram} />
        </Form.Item>

        <Form.Item
          className="app-form-item"
          label="Telegram"
          name="telegramName"
          rules={
            permissions.canEditNameAndTelegram
              ? [{ required: false }, { validator: validateTelegramUsername }]
              : undefined
          }
        >
          <Input placeholder="@username" disabled={!permissions.canEditNameAndTelegram} />
        </Form.Item>

        <Form.Item
          className="app-form-item"
          label="Departments"
          name="departmentIds"
          rules={
            permissions.canEditDepartments
              ? [{ required: true, message: 'Please select at least one department' }]
              : undefined
          }
        >
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select department(s)"
            optionFilterProp="children"
            disabled={!permissions.canEditDepartments}
          >
            {userMeta?.department?.map((department: { id: number; name: string }) => (
              <Option key={department.id} value={department.id}>
                {department.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          className="app-form-item"
          label={
            <span>
              Roles
              <Tooltip title="Click to view role descriptions" placement="left">
                <QuestionCircleOutlined
                  style={{ marginLeft: 6, color: '#888', cursor: 'pointer' }}
                  onClick={() => setRolesInfoVisible(true)}
                />
              </Tooltip>
            </span>
          }
          name="roles"
          rules={
            permissions.canEditRoles
              ? [{ required: true, message: 'Please select at least one role' }]
              : undefined
          }
        >
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select role(s)"
            optionFilterProp="children"
            disabled={!permissions.canEditRoles}
          >
            {ALL_DISPLAYABLE_ROLES.map((role) => {
              const canAssign = assignableRoles.includes(role.value);
              const isCurrentRole = filteredInitialRoles.includes(role.value);

              return (
                <Option key={role.value} value={role.value} disabled={!canAssign && !isCurrentRole}>
                  {role.label}
                  {!canAssign && !isCurrentRole && ' (no permission)'}
                </Option>
              );
            })}
          </Select>
        </Form.Item>
      </Form>

      <AppModal
        open={rolesInfoVisible}
        onCancel={() => setRolesInfoVisible(false)}
        title="Roles description"
        footer={null}
      >
        <Collapse
          accordion
          defaultActiveKey={rolesInfoData[0]?.title}
          items={rolesInfoData.map((role) => ({
            key: role.title,
            label: role.title,
            children: (
              <div className="ant-list ant-list-sm">
                <div className="ant-list-items">
                  {role.items.map((item) => (
                    <div className="ant-list-item" key={item}>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ),
          }))}
        />
      </AppModal>
    </AppModal>
  );
};
